import {
  CATEGORY_LABELS,
  EMPLOYMENT_TYPE_LABELS,
  PROFESSION_LABELS,
  PUBLISHER_TYPE_LABELS,
  REGION_LABELS,
  TENDER_TYPE_LABELS,
} from './taxonomy';
import type { DetailKey } from './taxonomy';
import type { ClosingWindow, FacetKey } from './types';

/**
 * Hebrew section headings for the 15 `details` fields on a posting page.
 *
 * THESE ARE NOT THE ETL's STRINGS, and the distinction matters. The vendored taxonomy carries a
 * `desc` for each field — but those are PROMPT TEXT, written to instruct the enrichment model
 * ('תקציר קצר (1-3 משפטים)' means "a short summary, 1–3 sentences"). Rendering one as a heading
 * would show a user the instruction that produced the content instead of naming it. UI copy is this
 * app's to own; the extraction vocabulary is the pipeline's.
 *
 * The parity assertion below is what keeps the two in step: if the ETL ever adds a 16th detail
 * field, this file stops compiling until someone writes a heading for it, so a new field cannot
 * reach the posting page as an unlabelled block.
 */
export const DETAIL_HEADINGS: Record<DetailKey, string> = {
  summary: 'תקציר',
  scope: 'היקף ההתקשרות',
  eligibility: 'תנאי סף',
  experience: 'ניסיון נדרש',
  technical: 'דרישות טכניות',
  submission: 'אופן ההגשה',
  pricing: 'מחיר וערבויות',
  clearance: 'סיווג ביטחוני',
  evaluation: 'אמות מידה לבחירה',
  contractPeriod: 'תקופת ההתקשרות',
  schedule: 'מועדים',
  updates: 'עדכונים והקלות',
  insurance: 'ביטוח',
  subcontracting: 'קבלני משנה',
  documentsRequired: 'מסמכים נדרשים',
  contactNotes: 'איש קשר',
};

/** Facet dimension headings for the filter sidebar. */
export const FACET_HEADINGS = {
  closing: 'מועד ההגשה',
  kind: 'סוג',
  category: 'תחום',
  region: 'אזור',
  tenderType: 'סוג מכרז',
  employmentType: 'היקף משרה',
  profession: 'מקצוע',
  publisherType: 'סוג מפרסם',
} as const;

/** Values that are not a taxonomy dimension but still need naming in the UI. */
export const KIND_LABELS: Record<'tender' | 'job', string> = { tender: 'מכרז', job: 'משרה' };

export const STATUS_LABELS: Record<'open' | 'closed' | 'delisted', string> = {
  open: 'פתוח',
  closed: 'נסגר',
  delisted: 'הוסר',
};

export const SORT_LABELS = { relevance: 'לפי התאמה', closing: 'נסגרים בקרוב', newest: 'חדשים' } as const;

/**
 * The deadline windows, phrased as one series.
 *
 * "עד X" throughout rather than a mix of phrasings, because these are read as a scale and not as
 * five unrelated options — and they have to stay short enough to sit on one line beside a count.
 * `ללא מועד` is the group that states no deadline at all, which is a real answer and not a gap.
 */
export const CLOSING_LABELS: Record<ClosingWindow, string> = {
  now: 'עד יומיים',
  week: 'עד שבוע',
  month: 'עד חודש',
  later: 'מעל חודש',
  none: 'ללא מועד',
};

/**
 * Every facet dimension's value labels, in one place.
 *
 * The sidebar and the active-filter chips both name the same values, and a second copy is how the
 * two end up disagreeing — one gets a wording change and the other keeps the old one, which reads
 * as two different filters for the same thing.
 */
export const FACET_VALUE_LABELS: Record<FacetKey, Record<string, string>> = {
  closing: CLOSING_LABELS,
  kind: KIND_LABELS,
  category: CATEGORY_LABELS,
  region: REGION_LABELS,
  tenderType: TENDER_TYPE_LABELS,
  employmentType: EMPLOYMENT_TYPE_LABELS,
  profession: PROFESSION_LABELS,
  publisherType: PUBLISHER_TYPE_LABELS,
};

/** Sidebar order, and the order the chips read in. Deadline leads both. */
export const FACET_ORDER: FacetKey[] = [
  'closing',
  'kind',
  'category',
  'region',
  'tenderType',
  'employmentType',
  'profession',
  'publisherType',
];
